import { useMemo } from 'react';

import {
  useGetTaskGuidelinesQuery,
  WorkflowGuidelinesResponse,
} from '@/services/api/mattersService';

interface UseTaskGuidelinesResult {
  guidelines: WorkflowGuidelinesResponse[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Fetch workflow guidelines (notes) for a task based on its canonical type.
 *
 * Returns firm-level and matter-level notes from workflows that apply to
 * the given task type. Used to display operational guidelines in the task detail view.
 *
 * @param matterId - The matter ID to look up
 * @param taskType - The canonical task type (e.g., 'Welcome Call', 'Retainer Agreement')
 */
export function useTaskGuidelines(
  matterId: string | undefined,
  taskType: string | undefined,
): UseTaskGuidelinesResult {
  const skip = !matterId || !taskType;

  const { data, isLoading, error } = useGetTaskGuidelinesQuery(
    // Provide defaults when skip is true - these won't be used since query is skipped
    { matterId: matterId ?? '', taskType: taskType ?? '' },
    { skip },
  );

  const guidelines = useMemo<WorkflowGuidelinesResponse[]>(() => {
    if (!data) return [];
    return data;
  }, [data]);

  return {
    guidelines,
    isLoading: skip ? false : isLoading,
    error,
  };
}
